import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const QuizResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalResults: 0,
    avgScore: 0
  });
  const [dateFilter, setDateFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchQuizResults = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      
      // Fix timezone issue for date filtering
      if (dateFilter) {
        // Create date range for the selected day in local timezone
        const selectedDate = new Date(dateFilter);
        const startOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0);
        const endOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59);
        
        params.append('startDate', startOfDay.toISOString());
        params.append('endDate', endOfDay.toISOString());
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/admin/quiz/results?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter results on frontend as well to ensure accuracy
      let filteredByDate = response.data.results || [];
      if (dateFilter) {
        const selectedDate = new Date(dateFilter);
        filteredByDate = filteredByDate.filter(result => {
          const resultDate = new Date(result.completedAt);
          return resultDate.getFullYear() === selectedDate.getFullYear() &&
                 resultDate.getMonth() === selectedDate.getMonth() &&
                 resultDate.getDate() === selectedDate.getDate();
        });
      }
      
      let percentageSum = 0;
      let countedResults = 0;
      filteredByDate.forEach((result) => {
        const totalMarks = (result.totalQuestions || 0) * 10;
        if (totalMarks > 0) {
          percentageSum += (result.score / totalMarks) * 100;
          countedResults += 1;
        }
      });

      setResults(filteredByDate);
      setStats({
        totalResults: filteredByDate.length,
        avgScore: countedResults > 0 ? percentageSum / countedResults : 0
      });
    } catch (error) {
      console.error('Error fetching quiz results:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
      }
      // Set empty results on error
      setResults([]);
      setStats({ totalResults: 0, avgScore: 0 });
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => {
    fetchQuizResults();
  }, [fetchQuizResults]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Ensure we're displaying the date in the local timezone
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateMobile = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPercentage = (score, totalQuestions) => {
    const totalMarks = (totalQuestions || 0) * 10;
    if (totalMarks <= 0) {
      return 0;
    }
    return (score / totalMarks) * 100;
  };

  const getCorrectAnswers = (score, totalQuestions) => {
    if (!totalQuestions) {
      return 0;
    }
    const correct = score / 10;
    return Math.max(0, Math.min(totalQuestions, Math.round(correct)));
  };

  const getScoreColor = (score, totalQuestions) => {
    const percentage = getPercentage(score, totalQuestions);
    if (percentage >= 80) return 'text-green-600 bg-green-50';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const filteredResults = results.filter(result =>
    result.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    result.userIP?.includes(searchTerm)
  );

  const clearFilters = () => {
    setDateFilter('');
    setSearchTerm('');
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header - Responsive */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 truncate">Quiz Results</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Monitor and analyze quiz performance</p>
            </div>
            
            {/* Stats Cards - Responsive */}
            <div className="flex flex-col xs:flex-row gap-2 sm:gap-4 lg:flex-shrink-0">
              <div className="bg-blue-50 px-3 sm:px-4 py-2 rounded-lg min-w-0">
                <div className="text-xs sm:text-sm text-blue-600">Total Results</div>
                <div className="text-lg sm:text-xl font-semibold text-blue-800">{stats.totalResults}</div>
              </div>
              <div className="bg-green-50 px-3 sm:px-4 py-2 rounded-lg min-w-0">
                <div className="text-xs sm:text-sm text-green-600">Average Score</div>
                <div className="text-lg sm:text-xl font-semibold text-green-800">
                  {stats.avgScore.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters - Responsive */}
        <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
          <div className="space-y-4">
            {/* Search and Date Filter Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search by Name or IP
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter username or IP address..."
                />
              </div>
              <div className="w-full sm:w-auto sm:min-w-[180px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Date
                </label>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Action Buttons Row */}
            <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
              <button
                onClick={clearFilters}
                className="flex-1 xs:flex-none bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
              >
                Clear Filters
              </button>
              <button
                onClick={fetchQuizResults}
                disabled={loading}
                className="flex-1 xs:flex-none bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center text-sm sm:text-base"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Results Content - Responsive */}
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8 sm:py-12">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 text-sm sm:text-base">Loading results...</p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <div className="text-gray-400 text-4xl sm:text-6xl mb-4">📊</div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">No results found</h3>
              <p className="text-gray-500 text-sm sm:text-base">
                {searchTerm || dateFilter ? 'Try adjusting your filters' : 'No quiz results available yet'}
              </p>
              {(searchTerm || dateFilter) && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-blue-600 hover:text-blue-800 font-medium text-sm sm:text-base"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time Spent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completed At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredResults.map((result) => (
                      <tr key={result._id || `${result.userName}-${result.completedAt}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {result.userName?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {result.userName || 'Anonymous'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(result.score, result.totalQuestions)}`}>
                            {getCorrectAnswers(result.score, result.totalQuestions)}/{result.totalQuestions || 0}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {Math.round(getPercentage(result.score, result.totalQuestions))}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {Math.floor(result.timeSpent / 60)}m {result.timeSpent % 60}s
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(result.completedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {result.userIP}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet Card View */}
              <div className="lg:hidden space-y-3 sm:space-y-4">
                {filteredResults.map((result) => (
                  <div 
                    key={result._id || `${result.userName}-${result.completedAt}`} 
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {/* User Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {result.userName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="text-sm sm:text-base font-medium text-gray-900 truncate">
                            {result.userName || 'Anonymous'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Score Badge */}
                      <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(result.score, result.totalQuestions)} flex-shrink-0`}>
                        {getCorrectAnswers(result.score, result.totalQuestions)}/{result.totalQuestions || 0}
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Percentage</div>
                        <div className="font-medium text-gray-900">
                          {Math.round(getPercentage(result.score, result.totalQuestions))}%
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Time Spent</div>
                        <div className="font-medium text-gray-900">
                          {Math.floor(result.timeSpent / 60)}m {result.timeSpent % 60}s
                        </div>
                      </div>
                      <div className="col-span-2 xs:col-span-1">
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Completed</div>
                        <div className="font-medium text-gray-900">
                          <span className="hidden sm:inline">{formatDate(result.completedAt)}</span>
                          <span className="sm:hidden">{formatDateMobile(result.completedAt)}</span>
                        </div>
                      </div>
                      <div className="col-span-2 xs:col-span-1">
                        <div className="text-gray-500 text-xs uppercase tracking-wide">IP Address</div>
                        <div className="font-mono text-gray-900 text-xs sm:text-sm break-all">
                          {result.userIP}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Results Summary - Responsive */}
        {filteredResults.length > 0 && (
          <div className="px-4 sm:px-6 pb-4">
            <div className="text-xs sm:text-sm text-gray-600 text-center">
              <div className="flex flex-col xs:flex-row xs:items-center xs:justify-center gap-1 xs:gap-2">
                <span>Showing {filteredResults.length} of {results.length} total results</span>
                {dateFilter && (
                  <span className="text-blue-600 font-medium">
                    for {new Date(dateFilter).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: window.innerWidth < 640 ? 'short' : 'long',
                      day: 'numeric'
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizResults;
